import { useMemo, useCallback } from 'react';
import {
  useTableManageColumns,
  useTableFilterSortSearchManager,
  useTableFilterSortData,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import {
  formatPercentage,
  generateStandardFilename,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import useHistory from '../../hooks/useHistory';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import messages from './messages';
import { useMutation, useQuery } from 'react-query';
import NaurService from '../../services/naurService';
import { determineCalculationMethod } from './utils';
import { NaurEventStatus } from '../../types/naur';
import { processTableFilterOptions } from '../../utils/table-helpers';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { CERTIFICATION_TYPES } from '../ManageCertifications/constants';
import useAbortController from '../../hooks/useAbortController';
import AffordableService from '../../services/affordableService';
import { MutationOptions } from 'react-query';
import moment from 'moment';
import { useNaurEventsDetailsForBuildings } from '../../hooks/data-fetching/useNaurEvents';

const naurService = new NaurService();

/**
 * Maps each sortable/filterable header ID to the path inside the row cell
 * objects that useTableFilterSortData should read.
 * All row cells follow the shape { value, variant } so paths end in ".value".
 */
const PROPERTY_PATH_MAP = {
  naurStatus: { path: 'naurStatus.value', dataType: 'string' },
  buildingNumber: { path: 'buildingNumber.value', dataType: 'string' },
  buildingBIN: { path: 'buildingBIN.value', dataType: 'string' },
  floorplan: { path: 'floorplan.value', dataType: 'string' },
  setAside: { path: 'setAside.value', dataType: 'string' },
  unitNumber: { path: 'unitNumber.value', dataType: 'string' },
  bedBathCount: { path: 'bedBathCount.value', dataType: 'string' },
  squareFootage: { path: 'squareFootage.value', dataType: 'number' },
  headOfHousehold: { path: 'headOfHousehold.value', dataType: 'string' },
  lastCertified: { path: 'lastCertified.value', dataType: 'date' },
  oiPercent: { path: 'oiPercent.value', dataType: 'number' },
  lastCertIncome: { path: 'lastCertIncome.value', dataType: 'number' },
  incomeLimit: { path: 'incomeLimit.value', dataType: 'number' },
  satisfyingUnitNumber: {
    path: 'satisfyingUnitNumber.value',
    dataType: 'string',
  },
  scheduledFloorplanConversion: {
    path: 'scheduledFloorplanConversion.value',
    dataType: 'date',
  },
  satisfyingUnitFloorplan: {
    path: 'satisfyingUnitFloorplan.value',
    dataType: 'string',
  },
  satisfyingUnitBedBath: {
    path: 'satisfyingUnitBedBath.value',
    dataType: 'string',
  },
  satisfyingUnitStatus: {
    path: 'satisfyingUnitStatus.value',
    dataType: 'string',
  },
  moveOutDate: { path: 'moveOutDate.value', dataType: 'date' },
  applicantName: { path: 'applicantName.value', dataType: 'string' },
  incomeAtMoveIn: { path: 'incomeAtMoveIn.value', dataType: 'number' },
};

/**
 * Main hook for UnitCompliance component
 */
export function useUnitCompliance({ intl, renderCardContent }) {
  const name = useUniqueTableName('naur-triggering-units');

  // Building cards logic
  const {
    cards: buildingCards,
    propertyBuildings,
    selectedProperty,
  } = useBuildingCardsData();
  const { selectedBuildingIds, handleSelectBuilding } = useSelectedBuildings();
  const navigationCards = useBuildingNavigationCards(
    buildingCards,
    renderCardContent,
  );

  // Fetch raw events and map to row objects (no sorting applied here)
  const { rows: allRows, isLoading } = useNAURRows({ selectedBuildingIds });

  // Derive dynamic filter options from the current data set
  const filterOptions = useBuildFilterOptions({ rows: allRows });

  // Build headers with injected filter options
  const headers = useNAURHeaders({ intl, filterOptions });

  // Sort / filter / search state management
  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    // Default: active events first (Open → Pending… → Satisfied alphabetically)
    initialOrderBy: 'naurStatus',
    initialOrder: 'ASC',
  });

  // Apply sort / filter / search to the full row set
  const rows = useTableFilterSortData({
    results: allRows,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  // Column management (show/hide columns)
  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  // CSV export
  const handleCSVButtonClick = useCSVExport({
    headers: filteredHeaders,
    rows,
  });

  return {
    // Building navigation cards
    navigationCards,
    propertyBuildings,
    selectedProperty,
    selectedBuildingIds,
    hasSelectedBuildings: selectedBuildingIds.length > 0,
    handleSelectBuilding,

    // Table props
    name,
    headers: filteredHeaders,
    rows,
    count: rows.length,
    totalCount: allRows.length,
    isLoading,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    handleColumnChange,
    onCSVButtonClick: handleCSVButtonClick,

    // Sort / filter / search handlers (passed through to <Table>)
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  };
}

/**
 * Custom hook for building cards data
 */
function useBuildingCardsData() {
  const selectedProperty = useSelectedProperty();
  const propertyNaurDetailsQuery = usePropertyComplianceDetailsQuery({
    propertyId: selectedProperty.id,
  });

  const buildings = propertyNaurDetailsQuery.data?.buildings || [];

  const cards = useMemo(() => {
    return buildings.map((building) => {
      const applicableFractionMethod =
        determineCalculationMethod(building).requiredFractionPercentage;

      const applicableFraction =
        applicableFractionMethod !== null &&
        applicableFractionMethod !== undefined
          ? formatPercentage(applicableFractionMethod / 100)
          : 'Not Provided';

      const buildingCR = building.buildingCompliance?.currentFraction;
      const currentFraction =
        buildingCR !== null && buildingCR !== undefined
          ? formatPercentage(buildingCR / 100)
          : 'Not Provided';

      return {
        currentFraction,
        applicableFraction,
        id: building.id,
        buildingName: building.name,
        isCompliant: building.buildingCompliance?.isCompliant ?? true,
        totalUnits: building.residentialUnitsCount,
        totalNonCompliantUnits: building.nonCompliantUnitsCount,
      };
    });
  }, [buildings]);

  // The cards drop applicableFractionUnit / applicableFractionFloorSpace, which the
  // initialization action needs for its gating rule, so the raw rows travel too.
  // selectedProperty rides along because this hook already holds it and AppContext
  // has no default value — a second useSelectedProperty() call in the container would
  // throw wherever this hook is mocked.
  return { cards, propertyBuildings: buildings, selectedProperty };
}

/**
 * Custom hook for multi-select building selection from URL params.
 * Stores selected IDs as repeated params: ?buildingIds[]=x&buildingIds[]=y
 */
function useSelectedBuildings() {
  const history = useHistory();

  const selectedBuildingIds = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return params.getAll('buildingIds');
  }, [history.location.search]);

  const handleSelectBuilding = useCallback(
    (buildingId) => {
      const params = new URLSearchParams(history.location.search);

      // Clear all selections when no id is provided (e.g. onChange passes null/undefined)
      if (buildingId == null) {
        params.delete('buildingIds');
        history.replace({
          pathname: history.location.pathname,
          search: params.toString(),
        });
        return;
      }

      const current = params.getAll('buildingIds');

      // Toggle: remove if already selected, add if not
      const next = current.includes(buildingId)
        ? current.filter((id) => id !== buildingId)
        : [...current, buildingId];

      params.delete('buildingIds');

      // If selection becomes empty after toggle, keep params clean
      if (next.length === 0) {
        history.replace({
          pathname: history.location.pathname,
          search: params.toString(),
        });
        return;
      }

      next.forEach((id) => params.append('buildingIds', id));

      history.replace({
        pathname: history.location.pathname,
        search: params.toString(),
      });
    },
    [history],
  );

  return {
    selectedBuildingIds,
    handleSelectBuilding,
  };
}

/**
 * Transform building cards data into NavigationCards format
 */
function useBuildingNavigationCards(buildingCards, renderContent) {
  return useMemo(() => {
    return buildingCards.map((building) => ({
      id: building.id,
      title: `Building ${building.buildingName}`,
      subheader: '',
      status: {
        variant: building.isCompliant ? 'approved' : 'error',
        tooltipText: building.isCompliant
          ? 'Building is compliant'
          : `${building.totalNonCompliantUnits} non-compliant unit${
              building.totalNonCompliantUnits !== 1 ? 's' : ''
            }`,
      },
      content: renderContent({ building }),
    }));
  }, [buildingCards, renderContent]);
}

/**
 * Derives multiselect filter options from the current row data set so the
 * filter dropdowns always reflect the values actually present in the table.
 */
function useBuildFilterOptions({ rows }) {
  return useMemo(() => {
    const options = {
      naurStatus: [],
      buildingNumber: [],
      buildingBIN: [],
      floorplan: [],
      setAside: [],
      bedBathCount: [],
      oiPercent: [],
      incomeLimit: [],
      satisfyingUnitBedBath: [],
      satisfyingUnitStatus: [],
      scheduledFloorplanConversion: [],
    };

    rows.forEach((row) => {
      if (row.naurStatus?.value) options.naurStatus.push(row.naurStatus.value);
      if (row.buildingNumber?.value)
        options.buildingNumber.push(row.buildingNumber.value);
      if (row.buildingBIN?.value)
        options.buildingBIN.push(row.buildingBIN.value);
      if (row.floorplan?.value) options.floorplan.push(row.floorplan.value);
      if (row.setAside?.value) options.setAside.push(row.setAside.value);
      if (row.bedBathCount?.value)
        options.bedBathCount.push(row.bedBathCount.value);
      if (row.oiPercent?.value != null)
        options.oiPercent.push(String(row.oiPercent.value));
      if (row.incomeLimit?.value != null)
        options.incomeLimit.push(String(row.incomeLimit.value));
      if (row.satisfyingUnitBedBath?.value)
        options.satisfyingUnitBedBath.push(row.satisfyingUnitBedBath.value);
      if (row.satisfyingUnitStatus?.value)
        options.satisfyingUnitStatus.push(row.satisfyingUnitStatus.value);
      if (row.scheduledFloorplanConversion?.value)
        options.scheduledFloorplanConversion.push(
          row.scheduledFloorplanConversion.value,
        );
    });

    // Deduplicate, sort, and convert to { text, value } objects
    processTableFilterOptions(options);

    return options;
  }, [rows]);
}

/**
 * Custom hook for NAUR table headers — full column set per ticket spec
 */
function useNAURHeaders({ intl, filterOptions = {} }) {
  return useMemo(
    () => [
      {
        id: 'naurStatus',
        label: intl.formatMessage(messages.naurStatus),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.naurStatus,
      },
      {
        id: 'buildingNumber',
        label: intl.formatMessage(messages.buildingNumber),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.buildingNumber,
      },
      {
        id: 'buildingBIN',
        label: intl.formatMessage(messages.buildingBIN),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.buildingBIN,
      },
      {
        id: 'floorplan',
        label: intl.formatMessage(messages.floorplan),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.floorplan,
      },
      {
        id: 'setAside',
        label: intl.formatMessage(messages.setAside),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.setAside,
      },
      {
        id: 'unitNumber',
        label: intl.formatMessage(messages.unitNumber),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'bedBathCount',
        label: intl.formatMessage(messages.bedBathCount),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.bedBathCount,
      },
      {
        id: 'squareFootage',
        label: intl.formatMessage(messages.squareFootage),
        dataType: 'number',
        sortable: true,
      },
      {
        id: 'headOfHousehold',
        label: intl.formatMessage(messages.headOfHousehold),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'lastCertified',
        label: intl.formatMessage(messages.lastCertified),
        dataType: 'date',
        sortable: true,
      },
      // {
      //   id: 'oiPercent',
      //   label: intl.formatMessage(messages.oiPercent),
      //   dataType: 'number',
      //   sortable: true,
      //   multiselect: true,
      //   filterOptions: filterOptions.oiPercent,
      // },
      {
        id: 'lastCertIncome',
        label: intl.formatMessage(messages.lastCertIncome),
        dataType: 'currency',
        sortable: true,
      },
      {
        id: 'incomeLimit',
        label: intl.formatMessage(messages.incomeLimit),
        dataType: 'currency',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.incomeLimit,
      },
      {
        id: 'satisfyingUnitNumber',
        label: intl.formatMessage(messages.satisfyingUnit),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'scheduledFloorplanConversion',
        label: intl.formatMessage(messages.scheduledFloorplanConversion),
        dataType: 'date',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.scheduledFloorplanConversion,
      },
      {
        id: 'satisfyingUnitFloorplan',
        label: intl.formatMessage(messages.satisfyingUnitFloorplan),
        dataType: 'string',
      },
      {
        id: 'satisfyingUnitBedBath',
        label: intl.formatMessage(messages.satisfyingUnitBedBath),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.satisfyingUnitBedBath,
      },
      {
        id: 'satisfyingUnitStatus',
        label: intl.formatMessage(messages.satisfyingUnitStatus),
        dataType: 'string',
        sortable: true,
        multiselect: true,
        filterOptions: filterOptions.satisfyingUnitStatus,
      },
      {
        id: 'moveOutDate',
        label: intl.formatMessage(messages.moveOutDate),
        dataType: 'date',
        sortable: true,
      },
      {
        id: 'applicantName',
        label: intl.formatMessage(messages.applicantName),
        dataType: 'string',
        sortable: true,
        searchable: true,
      },
      {
        id: 'incomeAtMoveIn',
        label: intl.formatMessage(messages.incomeAtMoveIn),
        dataType: 'currency',
        sortable: true,
      },
      {
        id: 'oiCert',
        label: intl.formatMessage(messages.oiCert),
        dataType: 'string',
      },
      {
        id: 'naurCert',
        label: intl.formatMessage(messages.naurCert),
        dataType: 'string',
      },
      // {
      //   id: 'actions',
      //   label: intl.formatMessage(messages.actions),
      //   dataType: 'string',
      // },
    ],
    [intl, filterOptions],
  );
}

/**
 * Maps a NAUR status enum value to a human-readable label.
 */
function getNaurStatusLabel(status) {
  switch (status) {
    case NaurEventStatus.OPEN:
      return 'Open';
    case NaurEventStatus.PENDING_APPLICANT:
      return 'Pending Applicant';
    case NaurEventStatus.PENDING_CERTIFICATION:
      return 'Pending Certification';
    case NaurEventStatus.PENDING_MOVE_IN:
      return 'Pending Move-In';
    case NaurEventStatus.SATISFIED:
      return 'Satisfied';
    default:
      return status ?? '---';
  }
}

// A missing amount is null rather than '0.00' or '---' so the currency cell renders its own
// dashes and the column's filter options and number sort treat the row as empty.
function toCurrencyValue(amount) {
  return amount == null ? null : Number(amount).toFixed(2);
}

function getIncomeLimit(aq) {
  return aq?.qualificationTestResults?.[aq.setAsideProgramId]?.incomeLimit;
}

/**
 * Custom hook for NAUR table rows — wired to real API data.
 *
 * Default sort: active events first (OPEN → PENDING_APPLICANT →
 * PENDING_CERTIFICATION), then SATISFIED, each group sorted by most
 * recent certificationCompletedAt descending.
 */
function useNAURRows({ selectedBuildingIds }) {
  const { naurEvents, isLoading, isError } =
    useNaurEventsDetailsForBuildings(selectedBuildingIds);

  const selectedProperty = useSelectedProperty();
  const { id: propertyId, organizationId } = selectedProperty;

  const { mutate: mutateOiCert } = useGenerateTICMutation({
    organizationId,
    propertyId,
    options: {
      onSuccess: () => {},
      onError: () => {},
    },
  });

  const handleDownloadOiCert = useCallback(
    ({
      affordableQualificationId,
      applicationId,
      certificationType,
      residentId,
      setAsideProgramId,
    }) => {
      mutateOiCert({
        affordableQualificationId,
        applicationId,
        documentTypeName: 'TN TIC',
        isRecertification: certificationType === CERTIFICATION_TYPES.RECERT,
        residentId,
        setAsideProgramId,
      });
    },
    [mutateOiCert],
  );

  const rows = useMemo(() => {
    return naurEvents.map((event) => {
      // Triggering household/unit data
      const { mainCus: triggeringHouseholdCustomer } = event.household ?? {};
      const { firstName: tFirstName, lastName: tLastName } =
        triggeringHouseholdCustomer ?? {};
      const hohNameParts = [tFirstName, tLastName].filter(Boolean);
      const hohName = hohNameParts.length > 0 ? hohNameParts.join(' ') : '---';

      const hohProfileUrl = triggeringHouseholdCustomer
        ? getUrlWithSelectedPropertyId(
            `/resident/${triggeringHouseholdCustomer.resident?.residentId}`,
          )
        : null;

      // Satisfying Unit data
      const { currentApplicationHousehold, hu } = event.satisfyingUnit ?? {};
      const { mainCus: satisfyingUnitApplicantCustomer } =
        currentApplicationHousehold ?? {};
      const { firstName: sFirstName, lastName: sLastName } =
        satisfyingUnitApplicantCustomer ?? {};
      const satisfyingUnitApplicantName =
        sFirstName && sLastName ? `${sFirstName} ${sLastName}` : null;

      const scheduledFloorplanConversion = event.satisfyingUnit
        ? '---'
        : event.ufps?.unit?.currentResidentHousehold?.ntv?.moveOutDate ?? '---';

      const satisfyingUnitMoveOutDate = hu
        ? hu.moveOutDate
        : scheduledFloorplanConversion;

      const { firstName: scFirstName, lastName: scLastName } =
        event.satisfyingCertification?.headOfHousehold ?? {};

      const satisfyingCertificationHeadOfHouseholdName =
        scFirstName && scLastName ? `${scFirstName} ${scLastName}` : null;

      const residentId =
        event.satisfyingCertification?.headOfHousehold?.resident?.residentId;

      const residentUrl = residentId
        ? getUrlWithSelectedPropertyId(`/resident/${residentId}`)
        : null;

      const applicationUrl = event.satisfyingUnit?.currentApplicationHousehold
        ?.currentApplicationId
        ? getUrlWithSelectedPropertyId(
            `/application/${event.satisfyingUnit.currentApplicationHousehold.currentApplicationId}`,
          )
        : null;

      const applicantInfo = {
        fullName:
          satisfyingUnitApplicantName ??
          satisfyingCertificationHeadOfHouseholdName ??
          '---',
        url: applicationUrl ?? residentUrl,
      };

      return {
        id: event.id,

        naurStatus: {
          variant: 'string',
          value: getNaurStatusLabel(event.status),
        },

        buildingNumber: {
          variant: 'string',
          value: event.building?.name ?? '---',
        },

        buildingBIN: {
          variant: 'string',
          value: event.building?.buildingIDNumber ?? '---',
        },

        floorplan: {
          variant: 'string',
          value: event.triggeringUnit.floorPlan.internalName ?? '---',
        },

        setAside: {
          variant: 'string',
          value: event.household.aq?.setAsideProgram?.setAsideName ?? '---',
        },

        unitNumber: {
          variant: 'string',
          value: event.triggeringUnit.number ?? '---',
        },

        bedBathCount: {
          variant: 'string',
          value: event.triggeringUnit.floorPlan
            ? `${event.triggeringUnit.floorPlan.nBeds}/${event.triggeringUnit.floorPlan.nBaths}`
            : '---',
        },

        squareFootage: {
          variant: 'number',
          value: event.triggeringUnit.floorPlan.grossSquareFeet ?? null,
        },

        headOfHousehold:
          hohName && hohProfileUrl
            ? { variant: 'link', value: hohName, to: hohProfileUrl }
            : { variant: 'string', value: hohName ?? '---' },

        lastCertified: {
          variant: 'date',
          value: event.household.aq?.certificationCompletionDate ?? null,
        },

        // oiPercent: {
        //   // TODO
        //   variant: 'number',
        //   value: event.triggeringUnit.oiPercent ?? null,
        // },

        lastCertIncome: {
          variant: 'currency',
          value: toCurrencyValue(event.household.aq?.hhAnnualIncome),
        },

        incomeLimit: {
          variant: 'currency',
          value: toCurrencyValue(getIncomeLimit(event.household.aq)),
        },

        satisfyingUnitNumber: {
          variant: 'string',
          value: event.satisfyingUnit?.number ?? '---',
        },

        scheduledFloorplanConversion: {
          variant: 'date',
          value: scheduledFloorplanConversion,
        },

        satisfyingUnitFloorplan: {
          variant: 'string',
          value: event.satisfyingUnit?.floorPlan.internalName ?? '---',
        },

        satisfyingUnitBedBath: {
          variant: 'string',
          value: event.satisfyingUnit?.floorPlan
            ? `${event.satisfyingUnit?.floorPlan.nBeds}/${event.satisfyingUnit?.floorPlan.nBaths}`
            : '---',
        },

        satisfyingUnitStatus: {
          variant: 'string',
          value: event.satisfyingUnit?.unitStatus.description ?? '---',
        },

        moveOutDate: {
          variant: 'date',
          value: satisfyingUnitMoveOutDate,
        },

        applicantName: applicantInfo.url
          ? {
              variant: 'link',
              value: applicantInfo.fullName,
              to: applicantInfo.url,
            }
          : { variant: 'string', value: applicantInfo.fullName },

        incomeAtMoveIn: {
          variant: 'currency',
          value: event.satisfyingCertification?.hhAnnualIncome ?? '---',
        },

        oiCert: event.triggeringCertificationId
          ? {
              variant: 'menu',
              iconName: 'DownloadIcon',
              closeOnClick: true,
              options: [
                {
                  text: 'Download',
                  value: 'download',
                  onClick: () =>
                    handleDownloadOiCert({
                      affordableQualificationId: event.household.aq?.id,
                      applicationId: event.household.currentApplicationId,
                      certificationType: event.household.aq?.certificationType,
                      residentId:
                        triggeringHouseholdCustomer.resident?.residentId,
                      setAsideProgramId: event.household.aq?.setAsideProgramId,
                    }),
                },
              ],
            }
          : { variant: 'string', value: '---' },

        naurCert: event.satisfyingCertification
          ? {
              variant: 'menu',
              iconName: 'DownloadIcon',
              closeOnClick: true,
              options: [
                {
                  text: 'Download',
                  value: 'download',
                  onClick: () =>
                    handleDownloadOiCert({
                      affordableQualificationId:
                        event.satisfyingCertification.id,
                      applicationId:
                        event.satisfyingCertification.applicationId,
                      certificationType:
                        event.satisfyingCertification.certificationType,
                      residentId:
                        event.satisfyingCertification.headOfHousehold.resident
                          ?.residentId,
                      setAsideProgramId:
                        event.satisfyingCertification.setAsideProgramId,
                    }),
                },
              ],
            }
          : { variant: 'string', value: '---' },

        // actions: {
        //   variant: 'menu',
        //   options: [
        //     {
        //       text: 'History',
        //       value: 'history',
        //     },
        //   ],
        // },
      };
    });
  }, [naurEvents, selectedProperty, handleDownloadOiCert]);

  return { rows, isLoading, isError };
}

/**
 * Custom hook for CSV export functionality
 */
function useCSVExport({ headers, rows }) {
  const selectedProperty = useSelectedProperty();

  return useCallback(() => {
    const fileName = generateStandardFilename({
      type: 'naur-triggering-units',
      descriptor: selectedProperty.name,
      extension: 'csv',
    });

    exportAndDownloadCSV({
      excludedHeaders: [],
      filteredHeaders: headers,
      fileName,
      rows,
    });
  }, [headers, rows, selectedProperty]);
}

const usePropertyComplianceDetailsQuery = ({
  propertyId,
  options = {},
}: {
  propertyId: ?string,
  options?: Object,
}) => {
  return useQuery(
    ['naur-property-compliance-details', propertyId],
    () => naurService.getPropertyComplianceDetails(propertyId),
    {
      enabled: Boolean(propertyId),
      ...options,
    },
  );
};

const useGenerateTICMutation = ({
  organizationId,
  propertyId,
  options,
}: {
  organizationId: string,
  propertyId: string,
  options: MutationOptions,
}) => {
  const affordableService = useMemo(() => new AffordableService(), []);
  const abortControllerOptions = useAbortController();

  return useMutation(
    (payload: {}) =>
      affordableService.generateTIC(
        organizationId,
        propertyId,
        payload,
        `TIC_${moment().unix()}.pdf`,
        abortControllerOptions,
      ),
    options,
  );
};
